import {inject, Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {getCookieOptions, User} from '@q9elements/ui-kit/common';
import {CookieService} from 'ngx-cookie-service';

import {Q9maCookieService} from './cookie.service';
import {LanguageSuggestionDialogComponent} from '../dialogs/language-suggestion/language-suggestion-dialog.component';

@Injectable({providedIn: 'root'})
export class LanguageSuggestionService {
  private readonly SUPPORTED_LANGUAGES = {
    en: 'English',
    ja: 'Japanese',
    uk: 'Ukrainian' // for test purposes
  };

  private readonly DEFAULT_LANGUAGE = 'en';
  private readonly LANGUAGE_PROPOSAL_COOKIE = 'lastLanguageProposed';
  private readonly COOKIE_TTL_DAYS = 7;
  private readonly COMFORT_TIME_BEFORE_LANGUAGE_ASK = 1000 * 3.5; // 3.5 seconds

  private dialog = inject(MatDialog);
  private cookieService = inject(CookieService);
  private translateService = inject(TranslateService);
  private q9CookieService = inject(Q9maCookieService);

  public getLanguageTitle(locale: string): string {
    return this.SUPPORTED_LANGUAGES[locale as keyof typeof this.SUPPORTED_LANGUAGES] || locale;
  }

  public restoreUserSavedLocale(user: Partial<User>): void {
    const locale = user.locale;

    if (!locale || !(locale in this.SUPPORTED_LANGUAGES)) {
      return;
    }

    if (this.translateService.currentLang !== locale) {
      this.translateService.use(locale);
      this.q9CookieService.setUserLocale(locale);
    }
  }

  public initializeLanguageSuggestion(user: Partial<User>): void {
    const userHasLocale = !!user.locale;
    const browserLang = navigator?.language?.toLowerCase();
    const userLangSupported =
      Object.keys(this.SUPPORTED_LANGUAGES).find(lang => browserLang?.startsWith(lang)) || null;
    const hasRecentProposal = this.hasRecentProposal();
    const isNotDefaultLang = userLangSupported !== this.DEFAULT_LANGUAGE;

    if (isNotDefaultLang && !userHasLocale && userLangSupported && !hasRecentProposal) {
      setTimeout(() => {
        this.dialog.open(LanguageSuggestionDialogComponent, {
          data: {
            browserLang: userLangSupported
          },
          width: '500px',
          maxWidth: '90vw',
          disableClose: false,
          autoFocus: true
        });
      }, this.COMFORT_TIME_BEFORE_LANGUAGE_ASK);
    }
  }

  setLanguageProposalCookie(): void {
    const expirationDate = new Date();

    expirationDate.setDate(expirationDate.getDate() + this.COOKIE_TTL_DAYS);

    this.cookieService.set(
      this.LANGUAGE_PROPOSAL_COOKIE,
      new Date().toISOString(),
      expirationDate,
      ...this.cookieOptions
    );
  }

  private hasRecentProposal(): boolean {
    if (!this.cookieService.check(this.LANGUAGE_PROPOSAL_COOKIE)) {
      return false;
    }

    const lastProposalStr = this.cookieService.get(this.LANGUAGE_PROPOSAL_COOKIE);
    const lastProposal = new Date(lastProposalStr);
    const now = new Date();
    const daysDiff = (now.getTime() - lastProposal.getTime()) / (1000 * 3600 * 24);

    return daysDiff < this.COOKIE_TTL_DAYS;
  }

  private get cookieOptions() {
    const {path, domain, secure, sameSite} = getCookieOptions();

    return [path, domain, secure, sameSite] as Array<string>;
  }
}
